import {create} from 'zustand'
import {Endereco, Feature, Filter, Imoveis, Projeto} from '../types'
import {
  CloseLoaderInfoOverlay,
  LoaderInfoOverlay,
} from '../../../components/UI/Loader/LoaderOverlay'
import Swal from 'sweetalert2'
import {ApiUrbit} from '../../../../infra/http/urbit'

type SegmentacaoValore = {
  area: [number[][], number[][]]
  preco: [number[][], number[][]]
  aluguel: [number[][], number[][]]
}

type StateProps = {
  imoveis: Imoveis[]
  endereco: Endereco
  filtro: Filter
  geometria: Feature
  projeto: Projeto
  geometriaSelecinada: Feature
  computados: number[]
  segmentacao: SegmentacaoValore
  fetchData: (
    geom: Feature,
    areaInfluencia: 'RAIO' | 'ZONAVALOR' | 'ISOCRONA' | 'DEFINIRMANUALMENTE',
    finalidade?: string
  ) => void
  setEndereco: (endereco: Endereco) => void
  setImoveis: (imoveis: Imoveis[]) => void
  setFiltro: (filtro: Filter) => void
  setGeometria: (geometria: Feature) => void
  setGeometriaSelecionada: (geometriaSelecinada: Feature) => void
  add: (id: number) => void
  remove: (id: number) => void
  clearData: () => void
  setSegmentacao: (setSegmentacao: SegmentacaoValore) => void
  updateData: (imoveis: Imoveis[]) => void
  setProjeto: (projeto: Projeto) => void
  getComputados: () => number[]
  setComputados: (ids: number[]) => void
  filtrarImoveis: (imoveis: Imoveis) => Imoveis
}

export const useImoveisStore = create<StateProps>((set, get) => ({
  imoveis: [],
  endereco: null,
  filtro: {
    raio: null,
    zonaValor: null,
    areaInfluencia: {
      tipo: null,
      valor: null,
    },
    distancia: null,
    dormitorios: null,
    faixaMetragem: null,
    faixaTicket: null,
    precoMedioM2: null,
    suites: null,
    tipologia: null,
    vagas: null,
  },
  geometria: null,
  geometriaSelecinada: null,
  computados: null,
  segmentacao: {
    area: [
      [
        [0, 30],
        [30, 45],
        [45, 60],
        [60, 90],
        [90, 120],
        [120, 150],
        [150, null],
      ],
      [
        [0, 120],
        [120, 170],
        [170, 220],
        [220, 270],
        [270, 320],
        [320, 370],
        [370, null],
      ],
    ],
    preco: [
      [
        [0, 255000],
        [255000, 350000],
        [350000, 500000],
        [500000, 750000],
        [750000, 1000000],
        [1000000, 1500000],
        [1500000, null],
      ],
      [
        [0, 1000000],
        [1000000, 1500000],
        [1500000, 2000000],
        [2000000, 2500000],
        [2500000, 3000000],
        [3000000, 3500000],
        [3500000, null],
      ],
    ],
    aluguel: [
      [
        [0, 2500],
        [2500, 3500],
        [3500, 4500],
        [4500, 5000],
        [5500, 7500],
        [7500, 8500],
        [8500, 10000],
        [10000, null],
      ],
      [
        [0, 2500],
        [2500, 3500],
        [3500, 4500],
        [4500, 5000],
        [5500, 7500],
        [7500, 8500],
        [8500, 10000],
        [10000, null],
      ],
    ],
  },
  projeto: null,
  fetchData: async (geom: Feature, areaInfluencia, finalidade = 'venda') => {
    LoaderInfoOverlay('Carregando imóveis...')
    // const centroid = turf.centroid(geom.geometry)
    // const lat = centroid.geometry.coordinates[1]
    // const lng = centroid.geometry.coordinates[0]

    if (!geom) {
      Swal.fire(
        '',
        'Erro ao tentar carregar um geometria, tente aplicar o filtro novamente.',
        'warning'
      )
      CloseLoaderInfoOverlay()
    }
    let geomSerach = null
    if (geom.type === 'Feature') {
      geomSerach = geom.geometry
    }

    const intersectString = btoa(unescape(encodeURIComponent(JSON.stringify(geomSerach))))
    const tipo = get().filtro.tipologia.join(',')
    // let url = `service/apartamentos-ofertas/${lng}/${lat}?distance=800&intersect=${intersectString}`
    // let url = `service/anuncios-venda/${lng}/${lat}?distance=800&intersect=${intersectString}`
    let url = `anuncios-usados?tipo=${tipo}&finalidade=${finalidade}&intersect=${intersectString}`
    const imoveis = await ApiUrbit.get(url)

    let imoveisAjustados = imoveis.itens.map((item: Imoveis) => {
      item.geom = JSON.parse(item.geom + '')
      item.vagas = Number(item.vagas)
      item.suites = Number(item.suites)
      item.quartos = Number(item.quartos)
      item.banheiros = Number(item.banheiros)
      item.active = true
      item.intersect = true
      item.valor = isNaN(Number(item.valor)) ? 0 : Number(item.valor)
      item.preco_m2 = item.valor / Number(item.area)
      item.valor_aluguel = Number(item.valor_aluguel)
      item.valor_aluguel_m2 =
        Number(item.area) > 0 && Number(item.valor_aluguel) > 0
          ? item.valor_aluguel / Number(item.area)
          : null
      item.aluguel_total = Number(item.aluguel_total)
      item.aluguel_total_m2 =
        Number(item.area) > 0 && Number(item.aluguel_total) > 0
          ? item.aluguel_total / Number(item.area)
          : null
      item = get().filtrarImoveis(item)
      return item
    })
    // let newData = imoveisAjustados.map((item: any) => {
    //     item = get().filtrarImoveis(item)
    //     return item
    // })

    set({imoveis: imoveisAjustados})
    CloseLoaderInfoOverlay()
  },
  setImoveis: (imoveis) => set((state) => ({imoveis: imoveis})),
  setSegmentacao: (segmentacao) => set((state) => ({segmentacao: segmentacao})),
  setEndereco: (endereco) => set((state) => ({endereco: endereco})),
  setFiltro: (filtro: Filter) => {
    set((state) => ({filtro: filtro}))
    let newData = get().imoveis.map((item: any) => {
      item = get().filtrarImoveis(item)
      return item
    })
    set((state) => ({imoveis: newData}))
  },
  filtrarImoveis: (item) => {
    let filtroDormitorios = true
    let filtro = get().filtro

    if (filtro) {
      if (filtro?.dormitorios?.length > 0) {
        if (filtro.dormitorios.indexOf(Number(item.quartos)) !== -1) {
          filtroDormitorios = true
        } else {
          filtroDormitorios = false
        }
      }
      let filtroVagas = true
      if (filtro?.vagas?.length > 0) {
        if (filtro.vagas.indexOf(Number(item.vagas)) !== -1) {
          filtroVagas = true
        } else {
          filtroVagas = false
        }
      }

      let filtroValor = true
      if (filtro?.faixaTicket) {
        if (item.valor >= filtro.faixaTicket[0] && item.valor <= filtro.faixaTicket[1]) {
          filtroValor = true
        } else {
          filtroValor = false
        }
      }

      let filtroPrecoM2 = true
      if (filtro?.precoMedioM2) {
        if (item.preco_m2 >= filtro.precoMedioM2[0] && item.preco_m2 <= filtro.precoMedioM2[1]) {
          filtroPrecoM2 = true
        } else {
          filtroPrecoM2 = false
        }
      }

      let filtroArea = true
      if (filtro?.faixaMetragem) {
        if (item.area >= filtro.faixaMetragem[0] && item.area <= filtro.faixaMetragem[1]) {
          filtroArea = true
        } else {
          filtroArea = false
        }
      }

      let filtroDistancia = true
      if (filtro?.distancia) {
        if (item.distancia >= filtro.distancia[0] && item.distancia <= filtro.distancia[1]) {
          filtroDistancia = true
        } else {
          filtroDistancia = false
        }
      }

      let filtroTipologia = true
      if (filtro.tipologia?.length > 0) {
        if (filtro.tipologia.includes(item.tipo)) {
          filtroTipologia = true
        } else {
          filtroTipologia = false
        }
      }

      let filtroLogradouro = true
      if (filtro.logradouro) {
        if (filtro.logradouro === item.endereco) {
          filtroLogradouro = true
        } else {
          filtroLogradouro = false
        }
      }

      let filtroMobiliado = true
      if (filtro.mobiliado) {
        if (filtro.mobiliado === item.mobiliado) {
          filtroMobiliado = true
        } else {
          filtroMobiliado = false
        }
      }

      // console.log('filtroDormitorios', '-', filtroDormitorios)
      // console.log('filtroVagas', '-', filtroVagas)
      // console.log('filtroValor', '-', filtroValor)
      // console.log('filtroPrecoM2', '-', filtroPrecoM2)
      // console.log('filtroArea', '-', filtroArea)
      // console.log('filtroDistancia', '-', filtroDistancia)
      // console.log('filtroTipologia', '-', filtroTipologia)
      if (
        filtroDormitorios &&
        filtroVagas &&
        filtroValor &&
        filtroPrecoM2 &&
        filtroArea &&
        filtroDistancia &&
        filtroTipologia &&
        filtroLogradouro &&
        filtroMobiliado
      ) {
        item.active = true
      } else {
        item.active = false
      }
      return item
    } else {
      return item
    }
  },
  setGeometria: (geometria) => set((state) => ({geometria: geometria})),
  setGeometriaSelecionada: (geometriaSelecinada: Feature) =>
    set(() => ({geometriaSelecinada: geometriaSelecinada})),
  add: (id: number) =>
    set((state) => ({
      imoveis: [
        ...state.imoveis.map((item) => {
          if (item.id === id) {
            item.active = true
          }
          return item
        }),
      ],
    })),
  remove: (id: number) =>
    set((state) => ({
      imoveis: [
        ...state.imoveis.map((item) => {
          if (item.id === id) {
            item.active = false
          }
          return item
        }),
      ],
    })),
  clearData: () => set((state) => ({imoveis: []})),
  updateData: (imoveis: Imoveis[]) => set({imoveis: imoveis}),
  setProjeto: (projeto: Projeto) => set({projeto: projeto}),
  getComputados: () => {
    let filtrado = get().imoveis.filter((item) => {
      return item.active
    })
    return filtrado.map((item) => item.id)
  },
  setComputados: (ids: number[]) => {
    set((state) => ({computados: ids}))
  },
}))
